import React, { PureComponent } from 'react';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import StatementBlock from './components/statementBlock';
import StatementPendingAmountTable from './components/statementPendingAmountTable';
import defaultProps from './statementTotalsDefaultProps';
import './statementTotals.scss';

interface Props extends IntlProps {
  currentBalanceType: string;
  data?: any;
  pendingDates?: any;
  emptyData?: boolean;
  columns?: any;
}

class StatementTotals extends PureComponent<Props> {
  static defaultProps = {
    data: defaultProps,
    pendingDates: {},
    columns: [],
  };

  render() {
    const { data, pendingDates, emptyData, currentBalanceType, columns } =
      this.props;
    const {
      pending,
      available,
      fundsReserved,
      accruedHoldbackVolume,
      netHoldbackVolume,
    } = data;

    const hideBlocks =
      columns.length > 0 &&
      columns.every((col) => +pending[col] === 0 && +available[col] === 0);

    columns.forEach((col) => {
      if (+pending[col] === 0 && +available[col] === 0) {
        available[col] = data.closing[col];
      }
    });

    return (
      <div
        className={classNames('statement-totals', {
          'statement-totals_no-data': emptyData,
        })}>
        {hideBlocks === false && (
          <StatementBlock
            title='statement.generalInfo.pendingAmount'
            data={pending}
            columns={columns}
            isHeader={true}>
            {isEmpty(pendingDates) === false && (
              <StatementPendingAmountTable
                title='statement.generalInfo.pending.period'
                columns={columns}
                data={pendingDates}
              />
            )}
          </StatementBlock>
        )}
        {this.renderTable(
          {
            rows: data.rowsOpeningClosing,
            opening: data.opening,
            openingTitle: 'openingBalance',
            closing: data.closing,
            closingTitle: 'closingBalance',
          },
          false
        )}
        {currentBalanceType !== 'out' &&
          this.renderTable(
            {
              rows: data.rowsReversedHoldback,
              opening: null,
              openingTitle: null,
              closing: null,
              closingTitle: null,
            },
            false
          )}
        {this.renderRow(
          {
            row: accruedHoldbackVolume,
            title: 'accruedHoldbackVolume',
          },
          false
        )}
        {this.renderRow(
          {
            row: netHoldbackVolume,
            title: 'netHoldbackVolume',
          },
          false
        )}
        {hideBlocks === false && (
          <StatementBlock
            title='statement.generalInfo.availableBalance'
            data={available}
            columns={columns}
            isHeader={false}
          />
        )}
        <StatementBlock
          title='statement.generalInfo.fundsReserved'
          data={fundsReserved}
          columns={columns}
          isHeader={false}
        />
      </div>
    );
  }

  renderRow = ({ row, title }, isHeader = true) => {
    const { columns, getTranslate } = this.props;

    return (
      <div className={classNames('statement-totals__table')}>
        <div className='statement-totals__balance-info statement-totals__single'>
          <div className='statement-totals__row'>
            <div className='statement-totals__col statement-totals__col_title statement-totals__single'>
              <span className='statement-totals__col-inner'>
                {getTranslate(`statement.generalInfo.${title}`)}
              </span>
            </div>
            {columns.map((item) => (
              <div
                className='statement-totals__col statement-totals__single'
                key={`opening-${item}`}>
                <div className='statement-totals__col-inner'>
                  {isHeader && (
                    <div className='statement-totals__col-title'>{item}</div>
                  )}
                  <div>{row[item]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  renderTable = (
    { rows, opening, closing, openingTitle, closingTitle },
    isHeader = true
  ) => {
    const { columns, getTranslate } = this.props;

    const mergeGroupsInRows = (rows) => {
      if (rows.length < 4) return rows;

      const newRows = [...rows];
      const mergedGroup = [...newRows[newRows.length - 2], ...newRows[newRows.length - 1]];

      newRows[newRows.length - 2] = mergedGroup;
      newRows.pop();

      return newRows;
    };

    return (
      <div className={classNames('statement-totals__table')}>
        {opening && (
          <div className='statement-totals__balance-info'>
            <div className='statement-totals__row'>
              <div className='statement-totals__col statement-totals__col_title'>
                {getTranslate(`statement.generalInfo.${openingTitle}`)}
              </div>
              {columns.map((item) => (
                <div className='statement-totals__col' key={`opening-${item}`}>
                  <div className='statement-totals__col-inner'>
                    {isHeader && (
                      <div className='statement-totals__col-title'>{item}</div>
                    )}
                    <div>{opening[item]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className='statement-totals__main'>
          {mergeGroupsInRows(rows).map((group, i) => (
            <div className='statement-totals__group' key={i}>
              {group.map((row) => (
                <div className='statement-totals__row' key={row.id}>
                  <div className='statement-totals__col statement-totals__col_title'>
                    <span className='statement-totals__col-inner'>
                      {getTranslate(`statement.generalInfo.${row.id}`)}
                    </span>
                  </div>
                  {columns.map((col, j) => (
                    <div
                      key={`${i}-${j}-${row.id}-${row.columns[col]}`}
                      className='statement-totals__col'>
                      <span className='statement-totals__col-inner'>
                        {row.columns[col]}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>

        {closing && (
          <div className='statement-totals__balance-info statement-totals__balance-info_closing'>
            <div className='statement-totals__row'>
              <div className='statement-totals__col statement-totals__col_title'>
                {getTranslate(`statement.generalInfo.${closingTitle}`)}
              </div>
              {columns.map((item) => (
                <div className='statement-totals__col' key={`closing-${item}`}>
                  <span className='statement-totals__col-inner'>
                    {closing[item]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
}

export default addTranslation(StatementTotals);
